import { Icon, Tooltip, Typography } from "@saviynt/design-system";
import { useNavigate } from "@tanstack/react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetCurrentUserRoles } from "../../shell-components/hooks/useGetCurrentUserRoles";

const DOCS_URL = "https://docs.saviyntcloud.com/";

interface Day0GetStartedSectionProps {
  /** Ref callback so the parent can scroll-snap to this section. */
  sectionRef: (el: HTMLElement | null) => void;
}

/** Screen 3 — get-started cards (connect a source, SSO, docs). */
export function Day0GetStartedSection({
  sectionRef,
}: Day0GetStartedSectionProps) {
  const intl = useIntl();
  const navigate = useNavigate();

  const { data: rolesData } = useGetCurrentUserRoles();
  const roles = rolesData?.roles ?? [];
  // SSO is an Admin-only action: enabled only for confirmed Admins, disabled
  // for everyone else (Viewers, other roles, and while roles load / on error).
  const isAdmin = roles.includes("ROLE_SAV_ISPMADMIN");
  const isViewer = roles.includes("ROLE_SAV_ISPMVIEWER");

  const goToIntegrations = () => navigate({ to: "/integrations" });
  const goToSso = () => navigate({ to: "/admin/settings/sso-settings" });

  // SSO card action — enabled only for Admins (disabled for everyone else).
  const ssoGo = (
    <span className="inline-flex">
      <button
        type="button"
        className="Onboarding__go"
        onClick={isAdmin ? goToSso : undefined}
        disabled={!isAdmin}
      >
        {intl.formatMessage({
          id: "SwsgEk",
          defaultMessage: "Configure",
          description: "Set up SSO card action",
        })}
        <Icon
          kind="ArrowRight"
          size="xSmall"
          color={
            isAdmin
              ? "--color-foreground-green-subtle"
              : "--color-foreground-neutral-subtle"
          }
        />
      </button>
    </span>
  );

  return (
    <section
      ref={sectionRef}
      className="relative isolate flex h-full snap-start snap-always flex-col items-center justify-center gap-8 overflow-hidden px-6 text-center"
    >
      <div className="flex flex-col items-center gap-2">
        <Typography
          kind="label"
          color="--color-foreground-green-subtle"
          className="uppercase !tracking-[0.14em]"
        >
          <FormattedMessage
            id="P5bv6d"
            description="Kicker above the onboarding get-started cards"
            defaultMessage="Get started"
          />
        </Typography>
        <Typography kind="h1" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="r+30kv"
            description="Heading on the onboarding get-started screen"
            defaultMessage="A few steps to full visibility"
          />
        </Typography>
      </div>

      <div className="flex w-full max-w-[46rem] flex-col gap-4">
        {/* Card 1 — Connect a source (prominent primary + what-to-expect flow) */}
        <article className="Onboarding__primary">
          <div className="flex items-start gap-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl Onboarding__marker--emerald">
              <Icon
                kind="Connectors"
                size="medium"
                color="--color-foreground-green-subtle"
              />
            </span>
            {/* Title + description stacked, so the copy starts under the title
                (aligned past the icon) rather than under the icon. */}
            <div className="flex flex-col gap-1.5">
              <Typography kind="h3" color="--color-foreground-neutral-bold">
                {isViewer ? (
                  <FormattedMessage
                    id="orynnF"
                    description="Explore sources card title for viewer role"
                    defaultMessage="Explore connected sources"
                  />
                ) : (
                  <FormattedMessage
                    id="kTZ9Fb"
                    description="Connect a source card title"
                    defaultMessage="Connect a source"
                  />
                )}
              </Typography>
              <Typography
                kind="body2"
                color="--color-foreground-neutral-medium"
              >
                {isViewer ? (
                  <FormattedMessage
                    id="gzChNN"
                    description="Connect a source card description for viewer role"
                    defaultMessage="Explore AI agents and non-human identities discovered from connected sources like AWS Bedrock, Microsoft Copilot, Google Gemini, and more."
                  />
                ) : (
                  <FormattedMessage
                    id="KnDQlW"
                    description="Connect a source card description"
                    defaultMessage="Integrate with AWS Bedrock, Microsoft Copilot, Google Gemini, and more to start discovering AI agents and non-human identities."
                  />
                )}
              </Typography>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            {isViewer ? (
              <button
                type="button"
                className="Onboarding__btn Onboarding__btn--primary max-sm:w-full max-sm:justify-center"
                onClick={() => navigate({ to: "/dashboard" })}
              >
                {intl.formatMessage({
                  id: "KX8/Pz",
                  defaultMessage: "View Dashboard",
                  description: "Connect a source card action for viewer role",
                })}
              </button>
            ) : (
              <button
                type="button"
                className="Onboarding__btn Onboarding__btn--primary max-sm:w-full max-sm:justify-center"
                onClick={goToIntegrations}
              >
                <Icon
                  kind="Add"
                  size="xSmall"
                  color="--color-foreground-neutral-inverse"
                />
                {intl.formatMessage({
                  id: "JVtfG5",
                  defaultMessage: "Add Integration",
                  description: "Connect a source card action",
                })}
              </button>
            )}
          </div>
        </article>

        {/* Card 2 — Set up SSO */}
        <article className="Onboarding__step">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl Onboarding__marker--purple">
            <Icon
              kind="SSO"
              size="medium"
              color="--color-foreground-purple-medium"
            />
          </span>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Typography
                kind="body1-bold"
                color="--color-foreground-neutral-bold"
              >
                <FormattedMessage
                  id="kzrdWT"
                  description="Set up SSO card title"
                  defaultMessage="Set up SSO"
                />
              </Typography>
            </div>
            <Typography kind="body2" color="--color-foreground-neutral-medium">
              <FormattedMessage
                id="KzumEy"
                description="Set up SSO card description"
                defaultMessage="Administrators can enable secure team sign-in by connecting an existing identity provider."
              />
            </Typography>
          </div>
          {isAdmin ? (
            ssoGo
          ) : (
            <Tooltip
              isPortalEnabled
              text={intl.formatMessage({
                id: "KnJb6p",
                defaultMessage: "Only Admins can configure SSO",
                description:
                  "Tooltip on disabled SSO action for non-admin users",
              })}
              placement="top"
              trigger={ssoGo}
            />
          )}
        </article>

        {/* Card 3 — Documentation */}
        <article className="Onboarding__step">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl Onboarding__marker--teal">
            <Icon
              kind="Document"
              size="medium"
              color="--color-foreground-teal-medium"
            />
          </span>
          <div className="flex flex-col gap-1.5">
            <Typography
              kind="body1-bold"
              color="--color-foreground-neutral-bold"
            >
              <FormattedMessage
                id="RYTuU+"
                description="Documentation card title"
                defaultMessage="Learn More About Zuma"
              />
            </Typography>
            <Typography kind="body2" color="--color-foreground-neutral-medium">
              <FormattedMessage
                id="hr6Mkj"
                description="Documentation card description"
                defaultMessage="Everything you need to know about getting started, connecting sources, and governing identities at scale."
              />
            </Typography>
          </div>
          <a
            className="Onboarding__go"
            href={DOCS_URL}
            target="_blank"
            rel="noreferrer"
          >
            {intl.formatMessage({
              id: "MkZiYO",
              defaultMessage: "Explore",
              description: "Documentation card action",
            })}
            <Icon
              kind="ArrowRight"
              size="xSmall"
              color="--color-foreground-green-subtle"
            />
          </a>
        </article>
      </div>
    </section>
  );
}
